/**
 * Refinance wizard submission controller (WI-TV-W5-004).
 *
 * The wizard's final required step performs one deliberate LeadIntake submission.
 * This module owns everything about that attempt except the DOM: it validates the
 * cumulative wizard answers, projects them through the existing bridge
 * (`toRefinanceFormState` -> `buildPayload` -> `buildLmdUpsertRequest`), posts a
 * single LeadIntake call, and emits the sanitized telemetry event and the
 * sanitized outward postMessage for the outcome.
 *
 * Guarantees enforced here:
 * - Exactly one LeadIntake call per deliberate attempt. The in-flight guard is
 *   raised synchronously at the top of {@link RefinanceSubmitter.submit}, before
 *   any `await`, so repeated activation (mouse click, Enter/Space keyboard
 *   activation, or the same event delivered again) collapses onto the single
 *   call already running.
 * - The in-flight guard is always released. Every operation performed after the
 *   guard is claimed runs inside a single outer `finally`, so a throwing
 *   correlation factory, client factory, payload build, LeadIntake call, or
 *   reporting sink cannot strand the guard and make later attempts dedupe forever.
 * - An accepted referral always resolves as a success. Everything after
 *   LeadIntake accepts — message id creation, complete-message and outcome preparation,
 *   telemetry, the outward notification — is total, for a synchronous throw and
 *   for a rejected promise returned by an `async` sink alike, so an accepted
 *   referral can never surface a false retry path or be submitted again.
 * - A message id is always available. When an injected id source fails, the id
 *   comes from an independent guarded entropy source and stays non-guessable and
 *   distinct per message; it is never a shared constant.
 * - No automatic replay. A failed attempt resolves to a recoverable outcome and
 *   nothing is retried here; only another deliberate `submit()` call issues one
 *   additional LeadIntake call.
 * - Once an attempt has succeeded the wizard is closed out: further calls are
 *   ignored so a completed referral can never be submitted twice.
 * - Correlation continuity: one correlation id per attempt is used for the
 *   LeadIntake request header, the telemetry event, and the outward postMessage,
 *   and is the id surfaced in the UI.
 * - The LeadIntake call alone decides the outcome. Observability and embedder
 *   notification run outside the call's own try/catch and are individually
 *   contained, so a throwing telemetry sink or embedder can never turn an
 *   accepted referral into a failure, strand the wizard, or reject `submit()`.
 * - Nothing borrower-identifying leaves this module. Only the existing sanitized
 *   error taxonomy, friendly messages, status class, and correlation/message ids
 *   reach telemetry or an embedder, and messages go only to configured allowed
 *   origins with an explicit `targetOrigin`.
 */
import {
  createCorrelationId,
  createLeadSubmissionClient,
  friendlyReferralMessage,
  isRetryableKind,
  ReferralApiError,
  type ApiErrorKind,
} from "../api";
import type { RuntimeConfig } from "../config";
import { buildLmdUpsertRequest } from "../form/lmd-mapper";
import { buildPayload } from "../form/payload-builder";
import { validateRefinance } from "../form/validation";
import {
  buildCompleteMessage,
  buildErrorMessage,
  createMessageId,
  notifyEmbedder,
} from "../messaging";
import type { LmdSubmissionResult } from "../models/lmd";
import { emitSubmitTelemetry, type TelemetrySink } from "../telemetry";
import { contain } from "../callbacks";
import { toRefinanceFormState } from "./refinance-values";

/** Wiring a submitter needs; every external effect is injectable for tests. */
export interface RefinanceSubmissionDeps {
  config: RuntimeConfig;
  fetchFn?: typeof fetch;
  telemetrySink?: TelemetrySink;
  /** Window whose embedder (parent/opener) is notified. Defaults to none. */
  win?: Window;
  correlationIdFactory?: () => string;
  messageIdFactory?: () => string;
}

/** The submitted referral was accepted by LMD. */
export interface RefinanceSubmissionSuccess {
  readonly status: "success";
  readonly correlationId: string;
  readonly messageId: string;
  readonly response: LmdSubmissionResult;
}

/**
 * The attempt reached LMD (or the network) and failed. The borrower stays on
 * the wizard and may deliberately retry; nothing is replayed automatically.
 */
export interface RefinanceSubmissionFailure {
  readonly status: "failed";
  readonly correlationId: string;
  readonly messageId: string;
  readonly kind: ApiErrorKind;
  readonly httpStatus?: number;
  readonly retryable: boolean;
  /** Sanitized, already-friendly message for this error kind. */
  readonly message: string;
}

/**
 * The cumulative answers are not submittable, so no LeadIntake call was made.
 * This is a local guard only: it emits no telemetry and no postMessage, because nothing
 * was submitted and the sanitized outcome taxonomy describes submits.
 */
export interface RefinanceSubmissionIncomplete {
  readonly status: "incomplete";
  /** Field-level messages from the authoritative refinance validator. */
  readonly errors: readonly string[];
}

/** A deliberate activation that collapsed onto the attempt already running. */
export interface RefinanceSubmissionDeduped {
  readonly status: "deduped";
}

/** An activation after a successful submission; the referral is closed out. */
export interface RefinanceSubmissionAlreadyCompleted {
  readonly status: "alreadyCompleted";
}

export type RefinanceSubmissionOutcome =
  | RefinanceSubmissionSuccess
  | RefinanceSubmissionFailure
  | RefinanceSubmissionIncomplete
  | RefinanceSubmissionDeduped
  | RefinanceSubmissionAlreadyCompleted;

export interface RefinanceSubmitter {
  /**
   * Runs one deliberate submission attempt for `values`. Resolves to `deduped`
   * without any side effect when an attempt is already in flight, and to
   * `alreadyCompleted` once a referral has been accepted.
   */
  submit(values: Readonly<Record<string, string>>): Promise<RefinanceSubmissionOutcome>;
  /** True while a LeadIntake call is outstanding. */
  readonly inFlight: boolean;
  /** True once an attempt has been accepted by LeadIntake. */
  readonly completed: boolean;
}

/**
 * The submittable-request result a {@link LeadRequestBuilder} returns: either
 * the built LMD request (with any partner reference to echo back on the
 * complete message) or the field-level validator errors that blocked it.
 */
export type LeadRequestResult =
  | { ok: true; request: ReturnType<typeof buildLmdUpsertRequest>; partnerReferenceId?: string }
  | { ok: false; errors: string[] };

/**
 * Projects the cumulative wizard answers, validates them with the authoritative
 * validator for the journey, and returns the LMD request when they are
 * submittable. This is the only journey-specific seam of the submitter; every
 * one-call/terminal/observer/messaging guarantee below is purpose-neutral.
 */
export type LeadRequestBuilder = (values: Readonly<Record<string, string>>) => LeadRequestResult;

/**
 * Validates the cumulative wizard answers with the authoritative refinance
 * validator and returns the LMD request when they are submittable.
 */
function buildRefinanceRequest(values: Readonly<Record<string, string>>): LeadRequestResult {
  const built = buildPayload(toRefinanceFormState(values));
  if (!built.ok) return { ok: false, errors: [built.error] };
  if (built.purpose !== "refinance") {
    return { ok: false, errors: ["The refinance wizard can only submit a refinance referral."] };
  }

  const errors = validateRefinance(built.request);
  if (errors.length > 0) return { ok: false, errors };

  const result: LeadRequestResult = {
    ok: true,
    request: buildLmdUpsertRequest(built),
  };
  if (built.request.partnerReferenceId) result.partnerReferenceId = built.request.partnerReferenceId;
  return result;
}

/** Distinguishes fallback ids even if every entropy source is unavailable. */
let fallbackSequence = 0;

/**
 * Builds a browser-safe, non-guessable message id from independent entropy.
 *
 * It deliberately does not call the shared `createMessageId` or the injected
 * factory: whichever produced the id request has already failed, and re-reading
 * a source that just failed risks a second failure or a second observable
 * invocation. Each part is guarded on its own and a monotonic sequence is mixed
 * in, so ids stay distinct across messages, attempts, and submitters even if
 * every entropy source is unavailable.
 */
function fallbackMessageId(): string {
  fallbackSequence += 1;
  return `msg-${guarded(() => Date.now().toString(36))}-${guarded(randomSuffix)}-${fallbackSequence.toString(36)}`;
}

/** Evaluates one id part, substituting a neutral marker if it throws. */
function guarded(part: () => string): string {
  try {
    const value = part();
    return value.length > 0 ? value : "0";
  } catch {
    return "0";
  }
}

/**
 * Non-guessable suffix from browser crypto, degrading to `Math.random`. Two
 * independent draws are combined so the id stays unguessable even if one source
 * is weak.
 */
function randomSuffix(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const values = cryptoObj.getRandomValues(new Uint32Array(2));
    return Array.from(values, (value) => value.toString(16).padStart(8, "0")).join("");
  }
  return `${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
}

/**
 * Produces the one message id for one id request. The primary generator is
 * invoked at most once — it is never retried and never re-read — and if it throws
 * or returns anything unusable (a promise, a blank string, a non-string) the id
 * comes from the independent {@link fallbackMessageId} source instead. Neither
 * path can become a rejection, and no exception detail is exposed.
 */
function totalMessageId(primary: () => string): string {
  let id: unknown;
  try {
    id = primary();
  } catch {
    return fallbackMessageId();
  }
  return typeof id === "string" && id.trim().length > 0 ? id : fallbackMessageId();
}

/**
 * Builds a lead submitter around a journey-specific {@link LeadRequestBuilder}.
 * Everything except the request build — the in-flight/terminal guards, the
 * single LeadIntake call, correlation continuity, contained best-effort telemetry
 * and embedder messaging, and the sanitized outcome taxonomy — is shared, so the
 * purchase and refinance journeys submit through identical guarantees.
 */
export function createLeadSubmitter(
  deps: RefinanceSubmissionDeps,
  buildRequest: LeadRequestBuilder,
): RefinanceSubmitter {
  let inFlight = false;
  let completed = false;

  const safeMessageId = (): string => totalMessageId(deps.messageIdFactory ?? createMessageId);
  let activeCorrelationId = "";
  let client: ReturnType<typeof createLeadSubmissionClient> | undefined;
  const submissionClient = (): ReturnType<typeof createLeadSubmissionClient> => {
    if (!client) {
      client = createLeadSubmissionClient(deps.config, {
        fetchFn: deps.fetchFn,
        correlationIdFactory: () => activeCorrelationId,
      });
    }
    return client;
  };

  async function submit(
    values: Readonly<Record<string, string>>,
  ): Promise<RefinanceSubmissionOutcome> {
    // Both guards are read and raised synchronously, before the first `await`,
    // so any number of activations delivered while an attempt runs — a second
    // click, an Enter keypress, or the same event dispatched again — can never
    // start a second LeadIntake call.
    if (completed) return { status: "alreadyCompleted" };
    if (inFlight) return { status: "deduped" };

    inFlight = true;
    try {
      return await runAttempt(values);
    } finally {
      // Everything after the guard is claimed lives inside this `finally`, so no
      // throw — from validation, the payload build, the correlation factory, the
      // client factory, the LeadIntake call itself, or the reporting that follows
      // it — can leave the guard raised and make every later attempt dedupe forever.
      inFlight = false;
    }
  }

  /**
   * One attempt, from validation through reporting. May reject on an unexpected
   * exception; the caller's `finally` restores the in-flight guard either way.
   */
  async function runAttempt(
    values: Readonly<Record<string, string>>,
  ): Promise<RefinanceSubmissionOutcome> {
    const built = buildRequest(values);
    if (!built.ok) return { status: "incomplete", errors: built.errors };

    const correlationId = (deps.correlationIdFactory ?? createCorrelationId)();
    activeCorrelationId = correlationId;

    const clientForAttempt = submissionClient();

    // Only the LeadIntake call is classified. Reporting happens afterwards,
    // outside this try/catch, so a throwing sink can never be read as an API failure.
    let response: LmdSubmissionResult | undefined;
    let apiError: unknown;
    try {
      response = await clientForAttempt.submitLead(built.request);
      // Recorded before any reporting runs: once LeadIntake accepts, the referral
      // is closed out even if a later best-effort side effect throws.
      completed = true;
    } catch (err) {
      // A failed attempt stays retryable, but nothing is replayed here; only a
      // further deliberate activation issues another call.
      apiError = err;
    }

    if (response) {
      // LeadIntake accepted the referral, so from here nothing may throw: the
      // outcome is already decided and a throw would surface a false retry path for a
      // referral that has in fact been submitted.
      const accepted = response;
      const messageId = safeMessageId();

      contain(() =>
        emitSubmitTelemetry(
          { outcome: "success", correlationId },
          { enabled: deps.config.telemetryEnabled, sink: deps.telemetrySink },
        ),
      );

      const embedder = deps.win;
      if (embedder) {
        // Preparing the sanitized complete message is contained together with
        // sending it, so a malformed response cannot break the outcome either.
        contain(() =>
          notifyEmbedder(
            embedder,
            buildCompleteMessage({
              correlationId,
              response: accepted,
              environment: deps.config.environment,
              debug: deps.config.debug,
              ...(built.partnerReferenceId ? { partnerReferenceId: built.partnerReferenceId } : {}),
              messageId,
            }),
            deps.config.allowedPrefillOrigins,
          ),
        );
      }

      return { status: "success", correlationId, messageId, response: accepted };
    }

    const messageId = safeMessageId();

    const kind: ApiErrorKind = apiError instanceof ReferralApiError ? apiError.kind : "unknown";
    const httpStatus = apiError instanceof ReferralApiError ? apiError.status : undefined;

    contain(() =>
      emitSubmitTelemetry(
        { outcome: kind, correlationId, ...(httpStatus === undefined ? {} : { status: httpStatus }) },
        { enabled: deps.config.telemetryEnabled, sink: deps.telemetrySink },
      ),
    );

    const embedder = deps.win;
    if (embedder) {
      contain(() =>
        notifyEmbedder(
          embedder,
          buildErrorMessage({ correlationId, reason: kind }),
          deps.config.allowedPrefillOrigins,
        ),
      );
    }

    return {
      status: "failed",
      correlationId,
      messageId,
      kind,
      ...(httpStatus === undefined ? {} : { httpStatus }),
      retryable: isRetryableKind(kind),
      message: friendlyReferralMessage(kind),
    };
  }

  return {
    submit,
    get inFlight() {
      return inFlight;
    },
    get completed() {
      return completed;
    },
  };
}

/** Creates the refinance submitter used by the refinance wizard/journey. */
export function createRefinanceSubmitter(deps: RefinanceSubmissionDeps): RefinanceSubmitter {
  return createLeadSubmitter(deps, buildRefinanceRequest);
}
